import React, {useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import PlanItemComponent from '../../components/PlanItemComponent/PlanItemComponent';
import './PlansPage.scss';
import {PlanItem} from '../../shared/interfaces';
import {PLAN_STATUS, PLAN_STATUS_ENUM} from '../../shared/enums';
import PieChartComponent from '../../components/ChartJs/PieChart/PieChartComponent';

const plansFile = "/plan.json";

const STATUS_COLORS: Record<string, string> = {
    completed: '#2e7d32',
    inProgress: '#ed6c02',
    notCompleted: '#d32f2f',
    ongoing: '#757575',
};

const STATUS_LABELS: Record<string, string> = {
    completed: 'Виконано',
    inProgress: 'Виконується',
    notCompleted: 'Не виконано',
    ongoing: 'Постійно',
};

function PlansPage() {
    const [plans, setPlans] = useState<PlanItem[]>([]);
    const [statusFilter, setStatusFilter] = useState('');
    const [executorFilter, setExecutorFilter] = useState('');
    const [deadlineFilter, setDeadlineFilter] = useState('');
    const [responsibleFilter, setResponsibleFilter] = useState('');
    const [filtersOpen, setFiltersOpen] = useState(false);

    const [statuses, setStatuses] = useState<string[]>([]);
    const [executors, setExecutors] = useState<string[]>([]);
    const [deadlines, setDeadlines] = useState<string[]>([]);
    const [responsibles, setResponsibles] = useState<string[]>([]);

    const [chartData, setChartsData] = useState<number[]>([]);

    useEffect(() => {
        const fetchPlanData = async () => {
            try {
                let completed = 0;
                let inProgress = 0;
                let notCompleted = 0;
                let ongoing = 0;

                const response = await fetch(plansFile);
                if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

                const data: PlanItem[] = await response.json();
                setPlans(data);

                const statusSet = new Set<string>();
                const executorSet = new Set<string>();
                const deadlineSet = new Set<string>();
                const responsibleSet = new Set<string>();

                data.forEach(item => {
                    statusSet.add(item.status);
                    executorSet.add(item.executor);
                    deadlineSet.add(item.execution_deadline);
                    switch (item.status) {
                        case PLAN_STATUS_ENUM.completed:
                            completed++;
                            break;
                        case PLAN_STATUS_ENUM.inProgress:
                            inProgress++;
                            break;
                        case PLAN_STATUS_ENUM.notCompleted:
                            notCompleted++;
                            break;
                        case PLAN_STATUS_ENUM.ongoing:
                            ongoing++;
                            break;
                        default:
                            break;
                    }
                    item.responsible_parties.forEach(r => responsibleSet.add(r));
                });

                setChartsData([completed, inProgress, notCompleted, ongoing]);
                setStatuses(Array.from(statusSet));
                setExecutors(Array.from(executorSet));
                setDeadlines(Array.from(deadlineSet));
                setResponsibles(Array.from(responsibleSet));
            } catch (error) {
                console.error("Error loading JSON:", error);
            }
        };

        fetchPlanData();
    }, []);

    const filteredPlans = useMemo(() => {
        let result = [...plans];
        if (statusFilter) {
            result = result.filter(plan => plan.status === statusFilter);
        }
        if (executorFilter) {
            result = result.filter(plan => plan.executor === executorFilter);
        }
        if (deadlineFilter) {
            result = result.filter(plan => plan.execution_deadline === deadlineFilter);
        }
        if (responsibleFilter) {
            result = result.filter(plan =>
                plan.responsible_parties.includes(responsibleFilter)
            );
        }
        return result;
    }, [statusFilter, executorFilter, deadlineFilter, responsibleFilter, plans]);

    const hasActiveFilters = statusFilter || executorFilter || deadlineFilter || responsibleFilter;
    const activeFilterCount = [statusFilter, executorFilter, deadlineFilter, responsibleFilter].filter(Boolean).length;

    const clearFilters = () => {
        setStatusFilter('');
        setExecutorFilter('');
        setDeadlineFilter('');
        setResponsibleFilter('');
    };

    const summaryCards = [
        {key: 'completed', count: chartData[0] || 0},
        {key: 'inProgress', count: chartData[1] || 0},
        {key: 'notCompleted', count: chartData[2] || 0},
        {key: 'ongoing', count: chartData[3] || 0},
    ];

    return (
        <div className="plans-page">
            {/* Summary Section */}
            <section className="plans-summary">
                <div className="summary-cards">
                    {summaryCards.map(({key, count}) => (
                        <div key={key} className={`summary-card summary-card--${key}`}>
                            <span className="summary-card__count">{count}</span>
                            <span className="summary-card__label">{STATUS_LABELS[key]}</span>
                            <div className="summary-card__accent" style={{backgroundColor: STATUS_COLORS[key]}} />
                        </div>
                    ))}
                </div>
                <div className="progress-bar-wrapper">
                    <PieChartComponent data={chartData} />
                </div>
            </section>

            {/* Filters Section */}
            <section className="plans-filters">
                <div className="plans-filters__header">
                    <button
                        className="plans-filters__toggle"
                        onClick={() => setFiltersOpen(!filtersOpen)}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M1 2h14M4 8h8M6 14h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        Фільтри
                        {activeFilterCount > 0 && (
                            <span className="plans-filters__badge">{activeFilterCount}</span>
                        )}
                        <svg
                            className={`plans-filters__chevron ${filtersOpen ? 'plans-filters__chevron--open' : ''}`}
                            width="12" height="12" viewBox="0 0 12 12" fill="none"
                        >
                            <path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                    <span className="plans-filters__count">
                        {filteredPlans.length} з {plans.length}
                    </span>
                </div>

                <div className={`plans-filters__body ${filtersOpen ? 'plans-filters__body--open' : ''}`}>
                    <div className="plans-filters__row">
                        <div className="filter-group">
                            <label className="filter-group__label">Статус</label>
                            <select
                                className="filter-group__select"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="">Всі статуси</option>
                                {statuses.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filter-group">
                            <label className="filter-group__label">Виконавець</label>
                            <select
                                className="filter-group__select"
                                value={executorFilter}
                                onChange={(e) => setExecutorFilter(e.target.value)}
                            >
                                <option value="">Всі виконавці</option>
                                {executors.map(executor => (
                                    <option key={executor} value={executor}>{executor}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filter-group">
                            <label className="filter-group__label">Термін</label>
                            <select
                                className="filter-group__select"
                                value={deadlineFilter}
                                onChange={(e) => setDeadlineFilter(e.target.value)}
                            >
                                <option value="">Всі терміни</option>
                                {deadlines.map(deadline => (
                                    <option key={deadline} value={deadline}>{deadline}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filter-group">
                            <label className="filter-group__label">Відповідальні</label>
                            <select
                                className="filter-group__select"
                                value={responsibleFilter}
                                onChange={(e) => setResponsibleFilter(e.target.value)}
                            >
                                <option value="">Всі відповідальні</option>
                                {responsibles.map(responsible => (
                                    <option key={responsible} value={responsible}>{responsible}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {hasActiveFilters && (
                        <button className="plans-filters__clear" onClick={clearFilters}>
                            Скинути фільтри
                        </button>
                    )}
                </div>
            </section>

            {/* Plan Items Grid */}
            <section className="plans-grid">
                {filteredPlans.length === 0 && plans.length > 0 && (
                    <div className="plans-grid__empty">
                        Немає заходів за обраними фільтрами
                    </div>
                )}
                {filteredPlans.map((item) => {
                    const originalIdx = plans.indexOf(item);
                    return (
                        <Link key={originalIdx} to={`/plan/${originalIdx}`} state={item} className="plans-grid__link">
                            <PlanItemComponent
                                number={item.action_number}
                                text={item.short_action_name}
                                status={PLAN_STATUS[item.status]}
                                executor={item.executor}
                                deadline={item.execution_deadline}
                            />
                        </Link>
                    );
                })}
            </section>
        </div>
    );
}

export default PlansPage;
